import { UiButtonComponent } from '@/ui/button/ui-button';
import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { UiRadioButtonComponent } from '@/ui/radio-button/ui-radio-button';
import { ControlValueAccessorInteropDirective } from '@/utils/angular/forms/reactive-forms/reactive-forms-signals-interop.utils';
import { translate } from '@/utils/i18n/translate.reactive-helpers';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { Component, inject, output, resource, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { AdvizeoBooleanControlGroupDirective } from '../../../directives/advizeo-boolean-controls-group/advizeo-boolean-control-group.directive';
import { AdvizeoBooleanControlDirective } from '../../../directives/advizeo-boolean-controls-group/advizeo-boolean-control.directive';
import type { ClassificationNodeId } from '../../../interfaces/Classification.interface';
import { InformationProvider } from '../../../providers/information/information.provider';
import {
    type AdvizeoModalButton,
    type AdvizeoModalComponent,
    injectAdvizeoModalInput
} from '../../advizeo-modal-spawner/advizeo-modal-spawner.component';
import { LoadingAndErrorComponent } from '../../loading-and-error/loading-and-error';
import { LoadingAndErrorResourceBindingDirective } from '../../loading-and-error/loading-and-error-resource-binding.directive';
import type { UserFavoriteClassificationWithBreadcrumbFlattened } from './favorite-perimeter.interface';
import { FavoritePerimeterService } from './service/favorite-perimeter.service';

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./favorite-perimeter-modal.scss'],
    selector: 'favorite-perimeter-modal',
    templateUrl: 'favorite-perimeter-modal.html',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        UiIconButtonComponent,
        AdvizeoBooleanControlDirective,
        UiRadioButtonComponent,
        TranslatePipe,
        AdvizeoBooleanControlGroupDirective,
        UiButtonComponent,
        LoadingAndErrorComponent,
        LoadingAndErrorResourceBindingDirective,
        ControlValueAccessorInteropDirective
    ]
})
export class FavoritePerimeterModalComponent implements AdvizeoModalComponent<{}, ClassificationNodeId> {
    private _favoritePerimeterService = inject(FavoritePerimeterService);
    private _informationProvider = inject(InformationProvider);
    private _translateService = inject(TranslateService);

    selectedFavoriteClassification: UserFavoriteClassificationWithBreadcrumbFlattened | null = null;

    title = translate('COMPONENTS.MAIN_CLASSIFICATION_NODE_SELECTOR.SAVED_FAVORITES_PERIMETERS');
    isDismissable = signal(true);
    buttons = signal<AdvizeoModalButton[]>([]);

    close = output<ClassificationNodeId | void>();
    input = injectAdvizeoModalInput<{}>();

    classifications = resource({
        defaultValue: [],
        loader: () =>
            firstValueFrom(
                this._favoritePerimeterService.getFavoriteActivities$().pipe(
                    map(classifications =>
                        classifications.map(classification => {
                            return {
                                favoriteId: classification.favoriteId,
                                userId: classification.userId,
                                projectId: classification.projectId,
                                classificationId: classification.classificationId,
                                classificationNodeId: classification.classificationNodeId,
                                classificationNodeType: classification.classificationNodeType,
                                createdAt: classification.createdAt,
                                breadcrumb: classification.breadcrumb.join(' > ')
                            } as UserFavoriteClassificationWithBreadcrumbFlattened;
                        })
                    )
                )
            )
    });

    validate() {
        if (isNotNullish(this.selectedFavoriteClassification)) {
            this.close.emit(this.selectedFavoriteClassification.classificationNodeId);
        }
    }
    cancel() {
        this.close.emit();
    }

    deleteFavoritePerimeter(classification: UserFavoriteClassificationWithBreadcrumbFlattened) {
        this._favoritePerimeterService
            .deleteFavoritePerimeter$(classification.favoriteId)
            .then(() => {
                this.selectedFavoriteClassification = null;
                this.classifications.reload();
                this._informationProvider.showToast(
                    this._translateService.instant('COMPONENTS.MAIN_CLASSIFICATION_NODE_SELECTOR.DELETION.SUCCESS'),
                    'success'
                );
            })
            .catch(() => {
                this._informationProvider.showToast(
                    this._translateService.instant('COMPONENTS.MAIN_CLASSIFICATION_NODE_SELECTOR.DELETION.ERROR'),
                    'error'
                );
            });
    }
}
